import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  clientZoom,
  contentTopAtRest,
  isUserVerticalMove,
  neededClearance,
  nextClearance,
  noteGesture,
  topmostUnder,
} from "../lib/timeline/questionClearance.js";

/** The floating question's text box (FloatingQuestion.jsx). */
export const QUESTION_SELECTOR = "[data-floating-question] button";
/** Forecast content that must stay clear of the question: trigger labels and pies, and the
 *  resolution marker. The TODAY / FORECAST lines are deliberately untagged — they run under
 *  the question behind a fade mask. */
export const CLEARS_QUESTION_SELECTOR = "[data-clears-question]";
/** How long the forecast takes to ease to a new clearance. */
export const CLEARANCE_EASE_MS = 180;

const easeOut = (t) => 1 - (1 - t) * (1 - t);
const reducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Keeps the forecast clear of the rail layout's floating question by pushing it down by
 * just the overlap (lib/timeline/questionClearance.js), and lets it relax back once the
 * overlap is gone. The returned `offset` is added to the spine height, so everything laid
 * out from the spine moves with it.
 *
 * Measured after every commit and whenever the question or the timeline resizes (a panel
 * width drag, a font load), so it follows zoom, display size, the rail panel and the window
 * without predicting any of them.
 *
 * Once the user scrolls the timeline vertically themselves (wheel, drag, arrow keys) the
 * clearance freezes, so wherever they put the forecast is where it stays, even under the
 * question. `resume` re-arms it and scrolls back to the top; it runs on CENTER and whenever
 * the active forecast changes (which includes a new run landing as the active forecast).
 *
 * @param {object} deps
 * @param {boolean} deps.enabled    the rail layout (the only one with a floating question)
 * @param {{current: HTMLElement | null}} deps.scrollRef the timeline's vertical scroll box
 * @param {string | null} deps.activeId the active forecast's id
 * @returns {{offset: number, resume: () => void}}
 */
export function useQuestionClearance({ enabled, scrollRef, activeId }) {
  // The eased clearance being rendered.
  const [shown, setShown] = useState(0);
  // The clearance the DOM was last committed with — what a measurement subtracts.
  const appliedRef = useRef(0);
  const shownRef = useRef(0);
  const targetRef = useRef(0);
  const enabledRef = useRef(enabled);
  const suspendedRef = useRef(false);
  const gestureRef = useRef(null);
  const measureRafRef = useRef(null);
  const easeRafRef = useRef(null);
  const observerRef = useRef(null);
  const observedQuestionRef = useRef(null);

  const stopEase = useCallback(() => {
    if (easeRafRef.current != null) cancelAnimationFrame(easeRafRef.current);
    easeRafRef.current = null;
  }, []);

  const easeTo = useCallback(
    (to) => {
      stopEase();
      const from = shownRef.current;
      if (from === to) return;
      if (reducedMotion()) {
        setShown(to);
        return;
      }
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, Math.max(0, (now - start) / CLEARANCE_EASE_MS));
        setShown(t >= 1 ? to : Math.round(from + (to - from) * easeOut(t)));
        easeRafRef.current = t < 1 ? requestAnimationFrame(tick) : null;
      };
      easeRafRef.current = requestAnimationFrame(tick);
    },
    [stopEase],
  );

  const measure = useCallback(() => {
    measureRafRef.current = null;
    const el = scrollRef.current;
    if (!el) return;
    if (suspendedRef.current) return;
    let needed = 0;
    const question = enabledRef.current ? document.querySelector(QUESTION_SELECTOR) : null;
    if (question) {
      const q = question.getBoundingClientRect();
      const box = el.getBoundingClientRect();
      const zoom = clientZoom(box.width, el.offsetWidth);
      const rects = Array.from(el.querySelectorAll(CLEARS_QUESTION_SELECTOR), (n) =>
        n.getBoundingClientRect(),
      );
      const top = topmostUnder(rects, q);
      if (top !== null) {
        needed = neededClearance({
          questionBottom: (q.bottom - box.top) / zoom,
          contentTop: contentTopAtRest({
            clientTop: top,
            originTop: box.top,
            zoom,
            scrollTop: el.scrollTop,
            appliedOffset: appliedRef.current,
          }),
        });
      }
    }
    const next = nextClearance(targetRef.current, needed);
    if (next === targetRef.current) return;
    targetRef.current = next;
    easeTo(next);
  }, [scrollRef, easeTo]);

  const schedule = useCallback(() => {
    if (measureRafRef.current != null) return;
    measureRafRef.current = requestAnimationFrame(measure);
  }, [measure]);

  // Every commit can move the forecast or the question, so every commit re-measures (at most
  // once a frame). The question remounts per forecast, so it is re-observed when it changes.
  useLayoutEffect(() => {
    appliedRef.current = enabled ? shown : 0;
    shownRef.current = shown;
    // Turning the feature on starts fresh: a scroll made while it was off is not a placement.
    if (enabled && !enabledRef.current) suspendedRef.current = false;
    enabledRef.current = enabled;
    if (typeof ResizeObserver !== "undefined") {
      if (!observerRef.current) {
        observerRef.current = new ResizeObserver(() => schedule());
        if (scrollRef.current) observerRef.current.observe(scrollRef.current);
      }
      const question = document.querySelector(QUESTION_SELECTOR);
      if (question !== observedQuestionRef.current) {
        if (observedQuestionRef.current) observerRef.current.unobserve(observedQuestionRef.current);
        if (question) observerRef.current.observe(question);
        observedQuestionRef.current = question;
      }
    }
    schedule();
  });

  // A vertical scroll the user made freezes the clearance. Listening at the document in the
  // capture phase sees the scroll box's own (non-bubbling) scroll events, and the arrow keys
  // before App's handler stops them.
  useEffect(() => {
    const inBox = (e) => {
      const el = scrollRef.current;
      return !!el && !!e.target && el.contains(e.target);
    };
    const note = () => {
      const el = scrollRef.current;
      if (!el) return;
      gestureRef.current = noteGesture(gestureRef.current, {
        now: performance.now(),
        scrollTop: el.scrollTop,
      });
    };
    const onWheel = (e) => {
      // Pinch-zoom arrives as a ctrl/⌘-wheel and is a zoom, not a pan.
      if (!e.ctrlKey && !e.metaKey && inBox(e)) note();
    };
    // A grab-drag that starts in the box keeps counting wherever the pointer then goes.
    let dragging = false;
    const onDown = (e) => {
      dragging = e.button === 0 && inBox(e);
      if (dragging) note();
    };
    const onMove = (e) => {
      if (dragging && e.buttons & 1) note();
    };
    const onUp = () => {
      dragging = false;
    };
    const onKey = (e) => {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") note();
    };
    const onScroll = (e) => {
      const el = scrollRef.current;
      if (!enabledRef.current || !el || e.target !== el || suspendedRef.current) return;
      if (
        isUserVerticalMove(gestureRef.current, { now: performance.now(), scrollTop: el.scrollTop })
      ) {
        suspendedRef.current = true;
        stopEase();
        // An ease cut short leaves the offset partway: make that the target, so a later
        // resume measures from where the forecast really is and can finish the move.
        targetRef.current = shownRef.current;
      }
    };
    // telemetry-gesture: passively watches the user's own scrolling to stop an easing animation; no operation
    document.addEventListener("wheel", onWheel, { capture: true, passive: true });
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("mouseup", onUp, true);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("wheel", onWheel, { capture: true });
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("mouseup", onUp, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [scrollRef, stopEase]);

  useEffect(
    () => () => {
      if (measureRafRef.current != null) cancelAnimationFrame(measureRafRef.current);
      if (easeRafRef.current != null) cancelAnimationFrame(easeRafRef.current);
      observerRef.current?.disconnect();
    },
    [],
  );

  const resume = useCallback(() => {
    suspendedRef.current = false;
    gestureRef.current = null;
    const el = scrollRef.current;
    if (el && el.scrollTop) el.scrollTop = 0;
    schedule();
  }, [scrollRef, schedule]);

  // A different forecast is a fresh view: the clearance applies again.
  useEffect(() => {
    resume();
  }, [activeId, resume]);

  return { offset: enabled ? shown : 0, resume };
}
